/**
 * FELVORA — ACTIVIDAD VULNERABLE · Mitigantes por factor e indicadores 139 Quáter / 400 Bis.
 *
 * sep-2026 · Acuerdo 115/2026: la Evaluación Basada en Riesgos debe registrar los MITIGANTES
 * implementados por factor y los INDICADORES de alerta vinculados a los arts. 139 Quáter
 * (financiamiento al terrorismo) y 400 Bis (recursos de procedencia ilícita) del CPF.
 *
 * La estructura (5 factores + catálogo de indicadores) es FIJA en el backend
 * (`av_ebr_calibracion.py`); aquí solo se calibra el contenido. Cada guardado deja bitácora.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Siren } from "lucide-react";

import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AvEbrMitigantes } from "@/components/riesgo/av/AvEbrMitigantes";
import { AvEbrIndicadores } from "@/components/riesgo/av/AvEbrIndicadores";

const ROLES_EDITAN = ["admin_entidad", "oficial_cumplimiento", "cumplimiento_inmo"];

export default function AvEbrControles() {
  const { user } = useAuth();
  const puedeEditar = ROLES_EDITAN.includes(user?.role);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/av/ebr/calibracion");
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="p-6 text-sm text-slate-500" data-testid="av-ebr-controles-cargando">Cargando mitigantes e indicadores…</div>;
  }

  const r = data.resumen || {};
  return (
    <div className="space-y-5" data-testid="av-ebr-controles">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="av-ebr-resumen-mitigantes">
          <p className="flex items-center gap-1.5 text-xs text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-700" /> Mitigantes registrados
          </p>
          <p className="text-2xl font-semibold text-slate-900">{r.mitigantes_total ?? 0}</p>
          <p className="text-xs text-slate-500">{r.mitigantes_implementados ?? 0} implementados</p>
        </div>
        <div className={`rounded-lg border p-4 ${r.factores_sin_mitigante ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}
          data-testid="av-ebr-resumen-sin-mitigante">
          <p className="text-xs text-slate-500">Factores sin mitigante</p>
          <p className="text-2xl font-semibold text-slate-900">{r.factores_sin_mitigante ?? 0}<span className="text-sm text-slate-400"> / 5</span></p>
          <p className="text-xs text-slate-500">Cada factor del EBR debe tener al menos uno</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="av-ebr-resumen-indicadores">
          <p className="flex items-center gap-1.5 text-xs text-slate-500">
            <Siren className="h-3.5 w-3.5 text-red-700" /> Indicadores activos
          </p>
          <p className="text-2xl font-semibold text-slate-900">
            {r.indicadores_activos ?? 0}<span className="text-sm text-slate-400"> / {r.indicadores_total ?? 0}</span>
          </p>
          <p className="text-xs text-slate-500">Arts. 139 Quáter y 400 Bis CPF</p>
        </div>
      </div>

      <AvEbrMitigantes factores={data.factores} estatus={data.catalogos?.estatus || []}
        puedeEditar={puedeEditar} onSaved={setData} />

      <AvEbrIndicadores indicadores={data.indicadores} articulos={data.catalogos?.articulos || []}
        puedeEditar={puedeEditar} onSaved={setData} />

      <p className="text-xs text-slate-500" data-testid="av-ebr-controles-fundamento">
        {data.fundamento}
        {data.actualizado?.fecha ? ` · Último cambio: ${data.actualizado.fecha}${data.actualizado.por ? ` por ${data.actualizado.por}` : ""}` : ""}
      </p>
    </div>
  );
}
